import React, { useMemo, useState, useCallback } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { FmThemeIsland } from '@/shared/ui/FmThemeProvider';

export interface FmCardProps {
  /** Header title. When absent (and no icon/subtitle/actions/extra), the card renders headless. */
  title?: React.ReactNode;
  icon?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Actions in the header right slot. */
  actions?: React.ReactNode;
  /** AntD alias for `actions` — same slot, backward-compat. Both render if both present. */
  extra?: React.ReactNode;
  /** Semantic padding density. Use `compact` for dense work surfaces and `comfortable` for normal panels. */
  density?: 'compact' | 'comfortable';
  /**
   * @deprecated Use `density="compact"` or `density="comfortable"` instead.
   * Migration alias retained for legacy AntD-card-shaped callers.
   */
  size?: 'small' | 'default';
  /** Shows a spinner overlay in the body area. */
  loading?: boolean;
  /** Adds the `.card--glass` translucent treatment. */
  glass?: boolean;
  /**
   * Forces the card's token polarity independent of the ambient route theme.
   * `'light'` renders a solid light data card even on a dark route (e.g. the
   * map workspace) so `--fm-d-surface`/`--fm-fg-*` resolve to their light
   * values ("chrome is glass, content is solid light"). The primitive owns the
   * `data-theme` flip so consumers never create a page-local theme island.
   */
  theme?: 'light' | 'dark';
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  greenTint?: boolean;
  /** Adds `.card--hoverable` for interactive card panels. Keep off for static dashboard panels. */
  hoverable?: boolean;
  /**
   * Constrains the card to a token-backed max height and scrolls its overflow
   * (docked/side-panel behavior). DS-owned so a dockable panel never sets a
   * page-local `maxHeight`/`overflowY` inline style.
   */
  scroll?: boolean;
  /**
   * @internal DS-internal style prop for migration; forbidden in route-owned code.
   * Prefer semantic `density`, `theme`, and section/table primitives.
   */
  style?: React.CSSProperties;
  /** Forwarded to the root `<section>` (interactive cards). */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** Forwarded to the root `<section>` (interactive cards). */
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  /** Forwarded to the root `<section>` (interactive cards). */
  tabIndex?: number;
  /** Forwarded to the root `<section>` (interactive cards). */
  role?: string;
  /** Accessible name for interactive cards (e.g. `role="link"` whole-card nav). */
  'aria-label'?: string;
  /**
   * @deprecated DS-internal AntD Card compatibility escape hatch; forbidden in
   * route-owned code because body padding/layout must come from `density`.
   */
  bodyStyle?: React.CSSProperties;
  /**
   * @deprecated DS-internal AntD Card compatibility escape hatch. Only
   * `styles.body` is honored during migration; route-owned code must not pass it.
   */
  styles?: { body?: React.CSSProperties };
  'data-testid'?: string;
  /**
   * @internal DS/migration class hook; forbidden for route-owned layout, colour,
   * spacing, and card chrome. Add a named variant instead.
   */
  className?: string;
  /** AntD-Card compat shim — accepted and ignored so legacy callers compile. */
  variant?: string;
  children?: React.ReactNode;
}

export interface FmCardBodyProps {
  children: React.ReactNode;
  flush?: boolean;
  /** @internal DS-internal class hook for nested card body structure only. */
  className?: string;
  /** @internal DS-internal migration style prop; forbidden in route-owned code. */
  style?: React.CSSProperties;
}

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/** Card body section that maps to the design-system `.card__body` class. */
const FmCardBody: React.FC<FmCardBodyProps> = ({ children, flush = false, className, style }) => (
  <div className={cx('card__body', flush && 'card__body--flush', className)} style={style}>
    {children}
  </div>
);

interface CardHeadProps {
  hasHead: boolean;
  collapsible: boolean;
  title: React.ReactNode;
  icon: React.ReactNode;
  subtitle: React.ReactNode;
  actions: React.ReactNode;
  extra: React.ReactNode;
  onToggleCollapsed: () => void;
  onHeadKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

const renderCardHead = ({
  hasHead,
  collapsible,
  title,
  icon,
  subtitle,
  actions,
  extra,
  onToggleCollapsed,
  onHeadKeyDown,
}: CardHeadProps): React.ReactNode => {
  if (!hasHead) return null;

  return (
    <div
      className="card__head"
      role={collapsible ? 'button' : 'presentation'}
      tabIndex={collapsible ? 0 : undefined}
      {...(collapsible
        ? {
            onClick: (event: React.MouseEvent<HTMLDivElement>) => {
              if ((event.target as HTMLElement).closest('.card__head-actions')) return;
              onToggleCollapsed();
            },
            onKeyDown: onHeadKeyDown,
          }
        : {})}
    >
      <div className="card__title">
        {icon ? <span className="fm-card__icon">{icon}</span> : null}
        <span className="fm-card__title-stack">
          <span>{title}</span>
          {subtitle ? <span className="card__sub">{subtitle}</span> : null}
        </span>
      </div>
      <div className="card__head-actions">
        {Array.isArray(actions) ? React.Children.toArray(actions) : actions}
        {Array.isArray(extra) ? React.Children.toArray(extra) : extra}
      </div>
      {collapsible ? (
        <span className="card__chevron" aria-hidden="true">
          <DownOutlined />
        </span>
      ) : null}
    </div>
  );
};

interface CardBodyParams {
  loading: boolean;
  hasBodyChild: boolean;
  resolvedBodyStyle: React.CSSProperties | undefined;
  children: React.ReactNode;
}

const getCardBody = ({
  loading,
  hasBodyChild,
  resolvedBodyStyle,
  children,
}: CardBodyParams): React.ReactNode => {
  if (loading) {
    return (
      <div className="card__loader">
        <span className="card__spinner" />
      </div>
    );
  }
  if (hasBodyChild) {
    return children;
  }
  return <FmCardBody style={resolvedBodyStyle}>{children}</FmCardBody>;
};

interface CardClassNameParams {
  density: 'compact' | 'comfortable';
  glass: boolean;
  loading: boolean;
  collapsible: boolean;
  greenTint: boolean;
  hoverable: boolean;
  collapsed: boolean;
  scroll: boolean;
  className: string | undefined;
}

const getCardClassNames = ({
  density,
  glass,
  loading,
  collapsible,
  greenTint,
  hoverable,
  collapsed,
  scroll,
  className,
}: CardClassNameParams): string =>
  cx(
    'card',
    density === 'compact' && 'card--sm',
    glass && 'card--glass',
    loading && 'card--loading',
    collapsible && 'card--collapsible',
    greenTint && 'card--green-tint',
    hoverable && 'card--hoverable',
    collapsed && 'card--collapsed',
    scroll && 'card--scroll',
    className,
  );

function computeHasHead(props: FmCardProps): boolean {
  return Boolean(props.title || props.icon || props.subtitle || props.actions || props.extra);
}

interface ResolvedCardDerived {
  size: 'small' | 'default';
  density: 'compact' | 'comfortable';
  glass: boolean;
  loading: boolean;
  collapsible: boolean;
  greenTint: boolean;
  hoverable: boolean;
  scroll: boolean;
  resolvedBodyStyle: React.CSSProperties | undefined;
}

/** Resolve prop defaults + derived layout values so the component body stays simple. */
function resolveCardDerived(props: FmCardProps): ResolvedCardDerived {
  const size = props.size ?? 'default';
  return {
    size,
    density: props.density ?? (size === 'small' ? 'compact' : 'comfortable'),
    glass: props.glass ?? false,
    loading: props.loading ?? false,
    collapsible: props.collapsible ?? false,
    greenTint: props.greenTint ?? false,
    hoverable: props.hoverable ?? false,
    scroll: props.scroll ?? false,
    resolvedBodyStyle: props.bodyStyle ?? props.styles?.body,
  };
}

interface FmCardComponent extends React.FC<FmCardProps> {
  Body: React.FC<FmCardBodyProps>;
}

/** Reusable shell card with optional icon, actions, and collapsible body. */
export const FmCard: FmCardComponent = (props) => {
  const {
    title,
    icon,
    subtitle,
    actions,
    extra,
    theme,
    defaultCollapsed = false,
    collapsed,
    onCollapsedChange,
    style,
    onClick,
    onKeyDown,
    tabIndex,
    role,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    className,
    children,
  } = props;
  const {
    density: resolvedDensity,
    glass,
    loading,
    collapsible,
    greenTint,
    hoverable,
    scroll,
    resolvedBodyStyle,
  } = resolveCardDerived(props);
  const hasHead = computeHasHead(props);

  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const resolvedCollapsed = collapsed ?? internalCollapsed;
  const noop = () => {};

  const hasBodyChild = useMemo(
    () =>
      React.Children.toArray(children).some(
        (child) => React.isValidElement(child) && child.type === FmCardBody,
      ),
    [children],
  );

  const handleCollapsedChange = useCallback(() => {
    const next = !resolvedCollapsed;
    if (collapsed === undefined) {
      setInternalCollapsed(next);
    }
    onCollapsedChange?.(next);
  }, [collapsed, resolvedCollapsed, onCollapsedChange]);

  const handleCollapsedKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleCollapsedChange();
      }
    },
    [handleCollapsedChange],
  );

  return (
    <FmThemeIsland theme={theme}>
    <section
      style={style}
      {...(onClick ? { onClick } : {})}
      {...(onKeyDown ? { onKeyDown } : {})}
      tabIndex={tabIndex}
      role={role}
      aria-label={ariaLabel}
      className={getCardClassNames({
        density: resolvedDensity,
        glass,
        loading,
        collapsible,
        greenTint,
        hoverable,
        collapsed: resolvedCollapsed,
        scroll,
        className,
      })}
      data-testid={dataTestId}
    >
      {renderCardHead({
        hasHead,
        collapsible,
        title,
        icon,
        subtitle,
        actions,
        extra,
        onToggleCollapsed: collapsible ? handleCollapsedChange : noop,
        onHeadKeyDown: collapsible ? handleCollapsedKeyDown : noop,
      })}
      {getCardBody({
        loading,
        hasBodyChild,
        resolvedBodyStyle,
        children,
      })}
    </section>
    </FmThemeIsland>
  );
};

FmCard.Body = FmCardBody;

export default FmCard;
